import appError from '#shared/util/appError.js';

/**
 * @typedef {object} ActivationOptions
 * @property {string} walletKey Activation code for products with Alpha license
 * @property {string} activationKey Activation code for products without Alpha licencse
 * @property {string} correlationID ID generated by IPM for identifying which product was bought by the user
 * @property {string} productSKU Product SKU ID (eg. PRW)
 */

/**
 * @typedef {object} ActivationProduct
 * @property {string} internalId Product internal ID (eg. PRW-00-001-12)
 */

/**
 * @typedef {object} ActivationResult
 * @property {string} event Activation event (eg. InsertWalletKey_<walletKey>_<productSKU>_<correlationID>)
 * @property {object} response Additional result details
 * @property {string} [response.resultCode] Result of the activation
 * @property {boolean} [response.success] Result of the activation
 */

/**
 * Load global object with a fallback
 */
const chromeObject = window.chrome || {};

/**
 * Start activation using Secure Browser activation API
 * @param {ActivationOptions} options
 * @returns {Promise<ActivationResult>}
 */
const activateWithBrowser = ({ walletKey, productSKU = '' } = {}) => {
	if (typeof walletKey === 'string' && walletKey && typeof productSKU === 'string') {
		const activation = {
			key: walletKey,
			method: 'startPro',
			event: `startPro_${walletKey}_${productSKU}`
		};
		return new Promise((resolve, reject) => {
			if (typeof chromeObject.avast?.licensing?.[activation.method] === 'function') {
				chromeObject.avast.licensing[activation.method](activation.key, (success) => {
					const resultObject = {
						event: activation.event,
						response: {
							success
						}
					};
					if (success) {
						resolve(resultObject);
					} else {
						reject(resultObject);
					}
				});
			} else {
				reject(`chrome.avast.licensing.[${activation.method}] is not a function`);
			}
		});
	} else {
		throw appError('Invalid method arguments to activateWithBrowser', {
			code: 'APP_FRONTEND_UTIL',
			origin: 'activateWithBrowser',
			detail: {
				walletKey,
				productSKU
			}
		});
	}
};

/**
 * Start activation using WebShield application
 * @param {ActivationOptions} options
 * @param {ActivationProduct[]} products
 * @returns {void}
 */
const activateWithWebShield = ({ walletKey, correlationID = '' } = {}, products = []) => {
	if (typeof walletKey === 'string' && walletKey && typeof correlationID === 'string') {
		setTimeout(() => {
			const internalIds = products.map((product) => product.internalId).join(',');
			const activationScript = document.createElement('script');
			activationScript.src = `/static/default/js/ws-activation.js?wk=${walletKey}&p_crd=${correlationID}&sku=${internalIds}`;
			document.body.appendChild(activationScript);
		}, 0);
	} else {
		throw appError('Invalid method arguments to activateWithWebShield', {
			code: 'APP_FRONTEND_UTIL',
			origin: 'activateWithWebShield',
			detail: {
				walletKey,
				correlationID
			}
		});
	}
};

/**
 * Initialize web platform activation
 * @param {ActivationOptions} options
 * @param {ActivationProduct[]} products
 * @returns {Promise<ActivationResult>}
 */
export default function activateProductWeb(options = {}, products = []) {
	activateWithWebShield(options, products);
	return activateWithBrowser(options, products);
}
